import React, { useCallback, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { LightModeProvider } from '@lux/core';
import { Text, Preset, FunctionColor } from '@lux/text';
import { ChevronRightS } from '@lux/zds-icons';
import { CarouselItem, ResponsiveCarousel } from '@lux-sandbox/carousel';
import { Translate, TranslateScope } from '@pegasus/translation';
import { getCampaignMobileImage } from '@browsing/campaign';
import { trackSelectContent, trackViewPromotion } from '@browsing/tracking';
import { ErrorBoundary } from '@browsing/components';

import { Campaign } from '@shared/types/mylounge';
import { getCategoryTabId } from '@client/redux/modules/mylounge';
import { CategoryTabIds } from '@shared/constants';

import { isClient } from '@pegasus/utils';
import { CarouselCardNewDealsToday } from './carousel-card-new-deals-today';

import * as S from './new-deals-carousel.styles';
import { useNewDealsToday } from './use-new-deals-today';

interface NewDealsCarouselProps {
    needsTopMargin?: boolean;
}

const NewDealsCarouselInternal: React.FC<NewDealsCarouselProps> = ({ needsTopMargin = false }) => {
    const { campaignsForNewDealsTodayCarousel } = useNewDealsToday();

    const categoryTabId = useSelector(getCategoryTabId);

    const visibleCampaigns = useRef(new Set<string>());
    const trackedCampaigns = useRef(new Set<string>());
    const trackingTimeout = useRef<NodeJS.Timeout | null>(null);

    const handleTrackCampaignInView = useCallback(
        (campaign: Campaign, isVisible: boolean) => {
            if (isVisible) {
                visibleCampaigns.current.add(campaign.campaignId);
            } else {
                visibleCampaigns.current.delete(campaign.campaignId);
            }

            if (trackingTimeout.current) {
                clearTimeout(trackingTimeout.current);
            }

            trackingTimeout.current = setTimeout(() => {
                if (visibleCampaigns.current.size > 0) {
                    const newCampaignsToTrack = Array.from(visibleCampaigns.current).filter(
                        campaignId => !trackedCampaigns.current.has(campaignId),
                    );

                    if (newCampaignsToTrack.length > 0) {
                        newCampaignsToTrack.forEach(campaignId => {
                            trackedCampaigns.current.add(campaignId);
                        });

                        const items = newCampaignsToTrack.map(campaignId => {
                            const campaignIndex = campaignsForNewDealsTodayCarousel.findIndex(
                                c => c.campaignId === campaignId,
                            );
                            return {
                                campaignIdentifier: campaignId,
                                categoryId: categoryTabId,
                                index: campaignIndex,
                            };
                        });

                        if (items.length > 0) {
                            trackViewPromotion({
                                element: 'deals_today_carousel',
                                creativeName: 'deals_today_carousel',
                                itemListName: 'deals_today_carousel',
                                items,
                            });
                        }
                    }
                }
            }, 500);
        },
        [categoryTabId, campaignsForNewDealsTodayCarousel],
    );

    useEffect(() => {
        return () => {
            if (trackingTimeout.current) {
                clearTimeout(trackingTimeout.current);
            }
        };
    }, []);

    const handleSeeAllClick = () => {
        if (isClient()) {
            window.location.href = `/event/${CategoryTabIds.NEW_DEALS_TODAY}`;
        }
        trackSelectContent('deals_today_carousel', 'click', {
            contentId: 'see_all',
        });
    };

    const handleScroll = () => {
        trackSelectContent('deals_today_carousel', 'swipe');
    };

    return (
        <S.NewDealsCarouselContainer $needsTopMargin={needsTopMargin}>
            <TranslateScope path="newDealsToday">
                <S.StyledLink data-testid="new-deals-link" onClick={handleSeeAllClick}>
                    <S.NewDealsBadge>
                        <LightModeProvider>
                            <Text preset={Preset.XS} bold color={FunctionColor.function100}>
                                <Translate message="label" />
                            </Text>
                        </LightModeProvider>
                    </S.NewDealsBadge>
                    <S.NewDealsCarouselHeader id="new-deals-carousel-header">
                        <S.TitleWithChevron>
                            <Text preset={Preset.XL} bold color={FunctionColor.function0}>
                                <Translate message="title" />
                            </Text>
                            <ChevronRightS color={FunctionColor.function0} aria-hidden="true" />
                        </S.TitleWithChevron>
                    </S.NewDealsCarouselHeader>
                </S.StyledLink>

                <ResponsiveCarousel
                    visibleItems={2.2}
                    visibleItemsXs={3.5}
                    visibleItemsMd={5.3}
                    visibleItemsLg={5.6}
                    aria-labelledby="new-deals-carousel-header"
                    previousSlideLabel={<Translate message="previous" raw />}
                    nextSlideLabel={<Translate message="next" raw />}
                    onScroll={handleScroll}
                    withOverflow
                >
                    {campaignsForNewDealsTodayCarousel.map((campaign, index) => {
                        const media = getCampaignMobileImage(campaign);
                        return (
                            <CarouselItem key={campaign.campaignId}>
                                <TranslateScope path="myLounge">
                                    <CarouselCardNewDealsToday
                                        campaign={campaign}
                                        media={media}
                                        index={index}
                                        onInView={handleTrackCampaignInView}
                                    />
                                </TranslateScope>
                            </CarouselItem>
                        );
                    })}
                </ResponsiveCarousel>
            </TranslateScope>
        </S.NewDealsCarouselContainer>
    );
};

export const NewDealsCarousel: React.FC<NewDealsCarouselProps> = props => (
    <ErrorBoundary componentName="NewDealsCarousel">
        <NewDealsCarouselInternal {...props} />
    </ErrorBoundary>
);
